import {
  generateTemporaryPassword,
  validatePassword,
} from "./UserManagement.support";
import {
  Box,
  IconButton,
  InputAdornment,
  Stack,
  Tooltip,
  useTheme,
} from "@mui/material";
import CopyButton from "components/common/CopyButton";
import TextField from "components/common/TextField";
import React from "react";
import { useState, useEffect, useRef } from "react";

const CHARACTER_DELAY_MS = 15;

const pseudoRandomCapitals = (length) =>
  Array.from({ length }, () =>
    String.fromCharCode(65 + Math.floor(Math.random() * 26))
  ).join("");

const passwordCharColour = (char) => {
  if (/[0-9]/.test(char)) return "#1565c0";
  if (/[^A-Za-z]/.test(char)) return "#c71585";
  return undefined;
};

// An input's text can only be one colour, so a coloured copy is drawn behind the
// input and the input's own text is transparent. Both share MUI's input class
// so the characters line up.
const ColourCodedInput = React.forwardRef(
  ({ className, value, onScroll, ...props }, ref) => {
    const theme = useTheme();
    const behindRef = useRef(null);
    return (
      <Box
        className={"monospace"}
        sx={{
          position: "relative",
          display: "flex",
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <Box
          ref={behindRef}
          aria-hidden
          className={className}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            overflow: "hidden",
            whiteSpace: "pre",
            pointerEvents: "none",
          }}
        >
          {[...(value ?? "")].map((char, i) => (
            <Box
              component={"span"}
              key={i}
              sx={{ color: passwordCharColour(char) }}
            >
              {char}
            </Box>
          ))}
        </Box>
        <input
          ref={ref}
          className={className}
          value={value}
          onScroll={(e) => {
            if (behindRef.current)
              behindRef.current.scrollLeft = e.target.scrollLeft;
            onScroll?.(e);
          }}
          style={{
            position: "relative",
            color: "transparent",
            caretColor: theme.palette.text.primary,
          }}
          {...props}
        />
      </Box>
    );
  }
);

// onChange is also called on mount with the generated password
const PasswordField = ({ onChange }) => {
  const [password, setPassword] = useState(generateTemporaryPassword);
  const [revealed, setRevealed] = useState(0);
  const revealing = revealed !== null;
  const error = validatePassword(password);

  const changePassword = (newPassword) => {
    setPassword(newPassword);
    onChange(newPassword);
  };

  useEffect(() => {
    onChange(password);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!revealing) return;
    if (revealed >= password.length) {
      setRevealed(null);
      return;
    }
    const timeout = setTimeout(
      () => setRevealed(revealed + 1),
      CHARACTER_DELAY_MS
    );
    return () => clearTimeout(timeout);
  }, [revealing, revealed, password.length]);

  return (
    <Stack
      className={"fade-in-slow"}
      direction={"row"}
      alignItems={"center"}
      gap={1}
    >
      <TextField
        label={"Password"}
        className={"monospace"}
        value={
          revealing
            ? password.slice(0, revealed) +
              pseudoRandomCapitals(password.length - revealed)
            : password
        }
        onChange={(e) => changePassword(e.target.value)}
        error={Boolean(error)}
        helperText={error}
        InputProps={{
          spellCheck: "false",
          readOnly: revealing,
          inputComponent: ColourCodedInput,
          endAdornment: (
            <InputAdornment position={"end"}>
              <Tooltip title={"Generate a new password"}>
                <span>
                  <IconButton
                    aria-label={"Generate a new password"}
                    disabled={revealing}
                    // The default colour is translucent and fades the emoji
                    sx={{ color: "common.black" }}
                    onClick={() => {
                      changePassword(generateTemporaryPassword());
                      setRevealed(0);
                    }}
                  >
                    {"🎲"}
                  </IconButton>
                </span>
              </Tooltip>
            </InputAdornment>
          ),
        }}
      />
      {/* inert also blocks keyboard focus; React 18 only passes it as a string */}
      <Box
        inert={revealing || !password ? "" : undefined}
        sx={{ opacity: revealing || !password ? 0.38 : 1 }}
      >
        <CopyButton content={password} tooltip={"Copy password"} />
      </Box>
    </Stack>
  );
};

export default PasswordField;
